import * as React from 'react';
import { RootState } from 'reducers';
import { OptionProps } from 'react-apollo';
import { StockFilterValuesQuery } from './__generated__/StockFilterValuesQuery';
import { Props } from './index';
import { createSelector } from 'reselect';
import { Option } from './components/Filter/components/Options';
import { InjectedTranslateProps, Translated } from 'shared/utils/translate';
import {
  // @translate
  Stocks_StockType,
  // @translate
  Stocks_StockRisk,
} from '../../../__generated__/globalTypes';
import RiskOption from './components/RiskOption';
import CapRange from './components/CapRange';
import { Range } from './consts';

export { StockFilterValuesQuery } from './__generated__/StockFilterValuesQuery';
export const selector = (state: RootState) => state.components.stockFilters;
type CapLimit = [number, string | undefined];
const risks: Stocks_StockRisk[] = [
  Stocks_StockRisk.LOW,
  Stocks_StockRisk.MEDIUM,
  Stocks_StockRisk.HIGH,
];
export interface Data {
  sectors: Option[];
  industries: Option[];
  types: Option<Stocks_StockType | null>[];
  capRanges: Option<Range>[];
  risks: Option<Stocks_StockRisk>[];
  priceChangePeriods: Option[];
  prices: Option[];
}
export type State = OptionProps<Props & InjectedTranslateProps, StockFilterValuesQuery>;

const getData = ({ data }: State) => {
  return data;
};
const investmentStocksSelector = createSelector([getData], (data) => {
  if (data && data.stocksRemote) {
    return {
      industries: data.stocksRemote.industries,
      sectors: data.stocksRemote.sectors,
    };
  }
  return null;
});
function getTranslate({
  ownProps,
}: OptionProps<Props & InjectedTranslateProps, StockFilterValuesQuery>) {
  return ownProps.t;
}

const getCapRangesFilter = (t: Translated) => {
  const capLimits: CapLimit[] = [
    [0, t('Nano')],
    [5e1, t('Micro')],
    [3e2, t('Small feminine')],
    [2e3, t('Medium feminine')],
    [1e4, t('Big feminine')],
    [2e5, t('Mega')],
    [Infinity, undefined],
  ];

  const formatedCapLimits = capLimits
    .sort(([a], [b]) => b - a)
    .reduce<Option<Range>[]>((result, [left], i, arr) => {
      if (i >= arr.length - 1) {
        return result;
      }
      const [right, title] = arr[i + 1];
      const [min, max] = left < right ? [left, right] : [right, left];
      return [
        ...result,
        {
          key: min,
          title: <CapRange title={title || ''} min={min * 1000000} max={max * 1000000} />,
          value: { min, max },
        },
      ];
    }, []);

  return [{ title: t('Any feminine') }, ...formatedCapLimits];
};

const getRisksFilter = (t: Translated) => {
  return [
    { title: t('Any male') },
    ...risks.map((risk) => ({
      title: <RiskOption level={risk} showIcon />,
      titleAsActual: <RiskOption level={risk} />,
      value: risk,
    })),
  ];
};

export const responseToProps = createSelector(
  [investmentStocksSelector, getTranslate],
  (investmentStocks, t) => {
    const defaultValues: Data = {
      sectors: [{ title: t('Any male') }],
      industries: [{ title: t('Any feminine') }],
      types: [
        { title: t('Filter stocks and ETF'), value: null },
        { title: t('Filter stocks only'), value: Stocks_StockType.STOCKS },
        { title: t('Filter ETF only'), value: Stocks_StockType.ETF },
      ],
      capRanges: getCapRangesFilter(t),
      risks: getRisksFilter(t),
      prices: [
        {
          title: t('Any price'),
          value: null,
        },
      ],
      priceChangePeriods: [{ title: t('For year'), value: 365 }],
    };
    if (investmentStocks) {
      const { sectors, industries } = investmentStocks;

      const value = {
        ...defaultValues,
        sectors: [
          ...defaultValues.sectors,
          ...sectors.map((title) => ({ key: title, title: title, value: title })),
        ],
        industries: [
          ...defaultValues.industries,
          ...industries.map((title) => ({ key: title, title: title, value: title })),
        ],
      };

      return value;
    }
    return defaultValues;
  }
);
